import React from "react";
import "@/styles/favorite-team.css";
import { Check, Shield, Star } from "lucide-react";
import { toast } from "sonner";

import TeamLogo from "@/components/TeamLogo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getConfiguredSeason } from "@/lib/season";
import { useAccount, useUpdateFavoriteTeam } from "@/queries/account";
import { useStandings } from "@/queries/standings";
import { Skeleton } from "@/components/ui/skeleton";

export default function FavoriteTeamDialog({ open, onOpenChange }) {
  const season = getConfiguredSeason();
  const { data: account } = useAccount();
  const { data: standings, isLoading } = useStandings(season, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const updateFavorite = useUpdateFavoriteTeam();
  const rows = Array.isArray(standings?.rows) ? standings.rows : [];

  const selectTeam = async (teamId) => {
    try {
      await updateFavorite.mutateAsync(teamId);
      toast.success(teamId ? "Favoritlag sparat" : "Favoritlag borttaget");
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara favoritlaget");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="native-v3-favorite-dialog max-h-[82dvh] w-[calc(100%-2rem)] max-w-md overflow-hidden rounded-[1.4rem] p-0">
        <DialogHeader className="native-v3-favorite-dialog-header">
          <span className="native-v3-favorite-dialog-icon"><Star aria-hidden="true" /></span>
          <div>
            <DialogTitle>Välj favoritlag</DialogTitle>
            <DialogDescription>Lag från Bauhausligan {season}</DialogDescription>
          </div>
        </DialogHeader>

        <div className="native-v3-favorite-team-list">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div className="native-v3-favorite-team-option" key={index} aria-hidden="true">
                <Skeleton className="h-10 w-10" />
                <span><Skeleton className="h-4 w-28" /><Skeleton className="mt-2 h-3 w-20" /></span>
              </div>
            ))
          ) : rows.map((row) => {
            const teamId = String(row.team_id || "");
            const selected = teamId === String(account?.favorite_team_id || "");
            return (
              <button
                type="button"
                key={teamId || row.team}
                className="native-v3-favorite-team-option"
                data-selected={selected ? "true" : "false"}
                disabled={updateFavorite.isPending || !teamId}
                onClick={() => selectTeam(teamId)}
              >
                <TeamLogo teamName={row.team} size={42} fallback={<Shield aria-hidden="true" />} />
                <span>
                  <strong>{row.team}</strong>
                  <small>{row.city || `${row.points ?? 0} poäng`}</small>
                </span>
                {selected ? <Check aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>

        {account?.favorite_team_id ? (
          <button
            type="button"
            className="native-v3-favorite-remove"
            disabled={updateFavorite.isPending}
            onClick={() => selectTeam(null)}
          >
            Ta bort favoritlag
          </button>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
